<template>
  <v-dialog v-model="model" max-width="720">
    <v-card>
      <v-card-title class="text-h6">
        {{ editItem ? "Edit IoT Device" : "Create IoT Device" }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-select
            v-model="form.deviceTypeId"
            :items="deviceTypeItems"
            item-title="name"
            item-value="deviceTypeId"
            label="Device Type *"
            :rules="[r.required]"
            :loading="loadingTypes"
          />

          <v-text-field
            v-model="form.deviceId"
            label="Device ID *"
            :rules="[r.required]"
          />

          <v-text-field
            v-model="form.name"
            label="Name *"
            :rules="[r.required]"
          />

          <v-textarea
            v-model="form.locationDescription"
            label="Location Description"
            auto-grow
          />

          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="form.latitude"
                label="Latitude"
                type="number"
                step="0.00000001"
                hint="e.g. -33.8688"
                persistent-hint
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.longitude"
                label="Longitude"
                type="number"
                step="0.00000001"
                hint="e.g. 151.2093"
                persistent-hint
              />
            </v-col>
          </v-row>

          <v-text-field
            v-model="form.cosmosContainerId"
            label="Cosmos Container ID"
          />

          <v-text-field
            v-model="form.cosmosPartitionId"
            label="Cosmos Partition ID"
          />

          <v-text-field
            v-model="form.serialNumber"
            label="Serial Number"
          />

          <v-text-field
            v-model="form.sigfoxId"
            label="Sigfox ID"
          />

          <v-text-field
            v-model="form.installationDate"
            label="Installation Date"
            type="datetime-local"
          />

          <v-text-field
            v-model="form.lastMaintained"
            label="Last Maintained"
            type="datetime-local"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ editItem ? "Save Changes" : "Create" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import iot from "@/api/iot";
import { useDialogStore } from "@/stores/dialog";

export default {
  name: "IoTDeviceForm",
  props: {
    modelValue: { type: Boolean, default: false },
    editItem: { type: Object, default: null }
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      model: this.modelValue,
      valid: false,
      saving: false,
      loadingTypes: false,
      deviceTypeItems: [],
      form: {
        deviceRowId: null,
        deviceTypeId: null,
        deviceId: "",
        name: "",
        locationDescription: "",
        cosmosContainerId: "",
        cosmosPartitionId: "",
        serialNumber: "",
        sigfoxId: "",
        installationDate: "",
        lastMaintained: ""
      },
      r: { required: v => !!v || "Required" }
    };
  },
  watch: {
    modelValue(v) {
      this.model = v;
      if (v) this.bootstrap();
    },
    model(v) {
      this.$emit("update:modelValue", v);
    },
    editItem: {
      handler(newVal) {
        if (newVal && this.model) {
          this.loadDevice(newVal.deviceRowId);
        }
      },
      immediate: true
    }
  },
  methods: {
    async bootstrap() {
      await this.loadDeviceTypes();
      if (this.editItem && this.editItem.deviceRowId) {
        await this.loadDevice(this.editItem.deviceRowId);
      } else {
        this.resetForm();
      }
    },
    async loadDeviceTypes() {
      this.loadingTypes = true;
      try {
        const rawTypes = await iot.getDeviceTypes();
        // Normalize property names (handle both PascalCase and camelCase from API)
        this.deviceTypeItems = (rawTypes || []).map(t => ({
          deviceTypeId: t.deviceTypeId || t.DeviceTypeId,
          name: t.name || t.Name || '',
          description: t.description || t.Description || ''
        }));
        if (this.deviceTypeItems.length === 0) {
        }
      } catch (err) {
        this.deviceTypeItems = [];
      } finally {
        this.loadingTypes = false;
      }
    },
    async loadDevice(deviceRowId) {
      try {
        const response = await iot.getDevice(deviceRowId);
        const device = response.data || response;

        this.form = {
          deviceRowId: device.deviceRowId || device.DeviceRowId,
          deviceTypeId: device.deviceTypeId || device.DeviceTypeId,
          deviceId: device.deviceId || device.DeviceId || "",
          name: device.name || device.Name || "",
          locationDescription: device.locationDescription || device.LocationDescription || "",
          latitude: this.formatDecimal(device.latitude ?? device.Latitude),
          longitude: this.formatDecimal(device.longitude ?? device.Longitude),
          cosmosContainerId: device.cosmosContainerId || device.CosmosContainerId || "",
          cosmosPartitionId: device.cosmosPartitionId || device.CosmosPartitionId || "",
          serialNumber: device.serialNumber || device.SerialNumber || "",
          sigfoxId: device.sigfoxId || device.SigfoxId || "",
          installationDate: this.formatDateTimeLocal(device.installationDate || device.InstallationDate),
          lastMaintained: this.formatDateTimeLocal(device.lastMaintained || device.LastMaintained)
        };
      } catch (err) {
      }
    },
    resetForm() {
      this.form = {
        deviceRowId: null,
        deviceTypeId: null,
        deviceId: "",
        name: "",
        locationDescription: "",
        latitude: "",
        longitude: "",
        cosmosContainerId: "",
        cosmosPartitionId: "",
        serialNumber: "",
        sigfoxId: "",
        installationDate: "",
        lastMaintained: ""
      };
    },
    formatDecimal(val) {
      if (val == null || val === "") return "";
      const n = Number(val);
      return isNaN(n) ? "" : String(n);
    },
    parseDecimal(str) {
      if (!str || String(str).trim() === "") return null;
      const n = parseFloat(str);
      return isNaN(n) ? null : n;
    },
    formatDateTimeLocal(date) {
      if (!date) return "";
      const d = new Date(date);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    parseDateTimeLocal(str) {
      if (!str) return null;
      return new Date(str);
    },
    close() {
      this.model = false;
      this.resetForm();
    },
    async save() {
      const ok = await this.$refs.form.validate();
      if (!ok) return;

      this.saving = true;
      try {
        const payload = {
          deviceTypeId: this.form.deviceTypeId,
          deviceId: this.form.deviceId,
          name: this.form.name,
          locationDescription: this.form.locationDescription || null,
          latitude: this.parseDecimal(this.form.latitude),
          longitude: this.parseDecimal(this.form.longitude),
          cosmosContainerId: this.form.cosmosContainerId || null,
          cosmosPartitionId: this.form.cosmosPartitionId || null,
          serialNumber: this.form.serialNumber || null,
          sigfoxId: this.form.sigfoxId || null,
          installationDate: this.parseDateTimeLocal(this.form.installationDate),
          lastMaintained: this.parseDateTimeLocal(this.form.lastMaintained)
        };

        if (this.editItem && this.editItem.deviceRowId) {
          await iot.updateDevice(this.editItem.deviceRowId, payload);
        } else {
          await iot.createDevice(payload);
        }

        this.$emit("saved");
        this.close();
      } catch (err) {
        const dialog = useDialogStore();
        await dialog.alert({
          title: "Error",
          message: err.response?.data?.message || err.message || "Failed to save device",
          variant: "error"
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

